import React, { useState, useEffect } from 'react';
import { Bug, X, RotateCcw, Download } from 'lucide-react';
import { logger, type LogEntry } from '../utils/logging';

interface ApiCall {
  id: string;
  timestamp: number;
  url: string;
  params: Record<string, string>;
  status: 'pending' | 'success' | 'error';
  response?: any;
  error?: string;
}

interface DebugPanelProps {
  apiCalls: ApiCall[];
  onClose: () => void;
}

export function DebugPanel({ apiCalls, onClose }: DebugPanelProps) {
  const [showLogs, setShowLogs] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);

  useEffect(() => {
    // Subscribe to log updates
    const unsubscribe = logger.subscribe(newLogs => {
      setLogs(newLogs);
    });

    // Initialize with current logs
    setLogs(logger.getLogs());

    return () => {
      unsubscribe();
    };
  }, []);

  const handleClearLogs = () => {
    logger.clearLogs();
  };

  const handleDownloadLogs = () => {
    const logData = JSON.stringify(logs, null, 2);
    const blob = new Blob([logData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hoa-calculator-logs-${new Date().toISOString()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (apiCalls.length === 0 && logs.length === 0) return null;

  return (
    <div className="fixed bottom-4 left-4 w-96 bg-gray-900 text-gray-100 rounded-lg shadow-xl overflow-hidden">
      <div className="p-3 bg-gray-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bug className="h-5 w-5 text-gray-400" />
          <h3 className="font-medium">Debug Panel</h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleDownloadLogs}
            className="p-1 hover:bg-gray-700 rounded"
            title="Download Logs"
          >
            <Download className="h-4 w-4 text-gray-400" />
          </button>
          <button
            onClick={handleClearLogs}
            className="p-1 hover:bg-gray-700 rounded"
            title="Clear Logs"
          >
            <RotateCcw className="h-4 w-4 text-gray-400" />
          </button>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-700 rounded"
          >
            <X className="h-4 w-4 text-gray-400" />
          </button>
        </div>
      </div>
      
      <div className="border-b border-gray-800">
        <div className="flex">
          <button
            className={`px-4 py-2 text-sm ${!showLogs ? 'bg-gray-800 text-white' : 'text-gray-400 hover:text-white'}`}
            onClick={() => setShowLogs(false)}
          >
            API Calls ({apiCalls.length})
          </button>
          <button
            className={`px-4 py-2 text-sm ${showLogs ? 'bg-gray-800 text-white' : 'text-gray-400 hover:text-white'}`}
            onClick={() => setShowLogs(true)}
          >
            Logs ({logs.length})
          </button>
        </div>
      </div>

      <div className="max-h-96 overflow-y-auto">
        {showLogs ? (
          <div>
            {logs.map((log, index) => (
              <div 
                key={index}
                className="p-3 border-t border-gray-800 hover:bg-gray-800/50"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">
                    {log.timestamp}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded ${
                    log.level === 'info' ? 'bg-blue-500/20 text-blue-300' :
                    log.level === 'warn' ? 'bg-yellow-500/20 text-yellow-300' :
                    'bg-red-500/20 text-red-300'
                  }`}>
                    {log.level.toUpperCase()}
                  </span>
                </div>
                
                <div className="space-y-2 text-sm">
                  <p className="text-gray-200 whitespace-pre-wrap">{log.message}</p>
                  
                  {log.context && (
                    <div>
                      <span className="text-gray-400">Context: </span>
                      <pre className="text-gray-200 overflow-x-auto whitespace-pre-wrap">
                        {JSON.stringify(log.context, null, 2)}
                      </pre>
                    </div>
                  )}
                  
                  {log.error && (
                    <div>
                      <span className="text-red-400">Error: </span>
                      <pre className="text-red-200 overflow-x-auto whitespace-pre-wrap">
                        {JSON.stringify(log.error, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div>
            {apiCalls.map((call) => (
              <div 
                key={call.id}
                className="p-3 border-t border-gray-800 hover:bg-gray-800/50"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">
                    {new Date(call.timestamp).toLocaleTimeString()}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded ${
                    call.status === 'pending' ? 'bg-blue-500/20 text-blue-300' :
                    call.status === 'success' ? 'bg-green-500/20 text-green-300' :
                    'bg-red-500/20 text-red-300'
                  }`}>
                    {call.status.toUpperCase()}
                  </span>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-400">URL: </span>
                    <span className="text-gray-200 break-all">{call.url}</span>
                  </div>
                  
                  <div>
                    <span className="text-gray-400">Params: </span>
                    <pre className="text-gray-200 overflow-x-auto whitespace-pre-wrap">
                      {JSON.stringify(call.params, null, 2)}
                    </pre>
                  </div>
                  
                  {call.response && (
                    <div>
                      <span className="text-gray-400">Response: </span>
                      <pre className="text-gray-200 overflow-x-auto whitespace-pre-wrap">
                        {JSON.stringify(call.response, null, 2)}
                      </pre>
                    </div>
                  )}
                  
                  {call.error && (
                    <div>
                      <span className="text-red-400">Error: </span>
                      <span className="text-red-200">{call.error}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}